import {
  Mark,
  PasteRuleMatch,
  markPasteRule,
  mergeAttributes,
} from '@tiptap/core'

export interface LinkOptions {
  openOnClick: boolean
  HTMLAttributes: Record<string, any>
  redirect?: (url: string) => void
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    link: {
      setLink: (attributes: LinkAttributes) => ReturnType
      toggleLink: (attributes: LinkAttributes) => ReturnType
      unsetLink: () => ReturnType
    }
  }
}

export type LinkAttributes =
  | {
      type: 'link'
      href: string
      target?: string | null
    }
  | {
      type: 'internal'
      targetId: string
      target?: string | null
    }

export const Link = Mark.create<LinkOptions>({
  name: 'link',
  priority: 1000,
  keepOnSplit: false,
  exitable: true,

  inclusive() {
    return true
  },

  addOptions() {
    return {
      openOnClick: true,
      HTMLAttributes: {
        class: 'rt-link',
      },
      slugs: {
        team: undefined,
        repo: undefined,
      },
      redirect: (_url: string) => {},
    }
  },

  addPasteRules() {
    return [
      markPasteRule({
        find: (text: string) => {
          const foundLinks: PasteRuleMatch[] = []

          if (text) {
            const protocols = ['http://', 'https://']
            const defaultProtocol = 'https://'

            const find = (text: string) =>
              Array.from(
                text.matchAll(
                  /(https?:\/\/[^\s]+)|(www\.[^\s]+)|([^\s]+\.[^\s]+)/gi
                )
              ).map((match) => ({
                value: match[0],
                start: match.index || 0,
                href: match[0],
              }))

            const links = find(text).filter((item) => {
              // Check if the link already has a protocol
              const hasProtocol = protocols.some((protocol) =>
                item.value.toLowerCase().startsWith(protocol.toLowerCase())
              )

              if (hasProtocol) {
                return true
              }

              // Handle www. links
              if (item.value.toLowerCase().startsWith('www.')) {
                item.href = `${defaultProtocol}${item.value}`
                return true
              }

              // Validate domain-like strings
              const domainRegex = /^[a-zA-Z0-9-]+\.[a-zA-Z]{2,}$/
              if (domainRegex.test(item.value)) {
                item.href = `${defaultProtocol}${item.value}`
                return true
              }

              return false
            })

            if (links.length) {
              links.forEach((link) =>
                foundLinks.push({
                  text: link.value,
                  data: {
                    type: 'link',
                    href: link.href,
                    target: '_BLANK',
                  },
                  index: link.start,
                })
              )
            }
          }

          return foundLinks
        },
        type: this.type,
        getAttributes: (match) => {
          const href = match.data?.href

          if (href) {
            return {
              href,
              type: 'link',
              target: '_BLANK',
            }
          }
          return null
        },
      }),
    ]
  },
  addAttributes() {
    return {
      targetId: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-target-id') ?? null,
        renderHTML: (attributes) => {
          if (!attributes.targetId) return {}
          return { 'data-target-id': attributes.targetId }
        },
        keepOnSplit: true,
      },
      type: {
        default: 'link',
        parseHTML: (element) =>
          element.getAttribute('data-link-type') ?? 'link',
        renderHTML: (attributes) => {
          if (!attributes.type) return {}
          return { 'data-link-type': attributes.type }
        },
        keepOnSplit: true,
      },
      target: {
        default: undefined,
        parseHTML: (element) => element.getAttribute('target'),
        renderHTML: (attributes) => {
          if (!attributes.target) return {}
          return { target: attributes.target }
        },
        keepOnSplit: true,
      },
      href: {
        default: '',
        parseHTML: (element) => element.getAttribute('href'),
        renderHTML: (attributes) => {
          if (!attributes.href) return {}
          return { href: attributes.href }
        },
        keepOnSplit: true,
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: `a[data-link-type="internal"]`, // Only match internal links
        priority: 600,
        getAttrs: (dom) => {
          if (!dom) return false
          const targetId = dom.getAttribute('data-target-id')
          if (!targetId) return false
          return {
            targetId,
            internalLink: true,
            target: dom.getAttribute('target') || '_blank',
          }
        },
      },
      {
        tag: 'a[href]',
        priority: 500,
        getAttrs: (dom) => {
          if (!dom) return false
          const href = dom.getAttribute('href')
          if (!href) return false
          return {
            href,
            target: dom.getAttribute('target') || '_blank',
            internalLink: false,
            targetId: null,
          }
        },
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'a',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      0,
    ]
  },

  addCommands() {
    return {
      setLink:
        (attributes: LinkAttributes) =>
        ({ chain }) => {
          return chain()
            .setMark(this.name, attributes)
            .setMeta('preventAutolink', true)
            .run()
        },

      toggleLink:
        (attributes: LinkAttributes) =>
        ({ chain }) => {
          return chain()
            .toggleMark(this.name, attributes, { extendEmptyMarkRange: true })
            .setMeta('preventAutolink', true)
            .run()
        },

      unsetLink:
        () =>
        ({ chain }) => {
          return chain()
            .unsetMark(this.name, { extendEmptyMarkRange: true })
            .setMeta('preventAutolink', true)
            .run()
        },
    }
  },
})

//
